import React from 'react';
import styled, { stylesheet } from 'astroturf/react';
import { Popover, PopoverMode, PopoverProps } from 'js/design_system/Popover';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  background: transparent;
  gap: 0;
`;

const containerStyles = stylesheet`
  .top, .top-start, .top-end {
    flex-direction: column;
  }
  .left, .left-start, .left-end {
    flex-direction: row;
  }
  .right, .right-start, .right-end {
    flex-direction: row-reverse;
  }
  .bottom, .bottom-start, .bottom-end {
    flex-direction: column-reverse;
  }
`;

const TooltipContainer = styled.div`
  display: flex;
  padding: 8px var(--button-sm-standard-top-bottom-padding, 12px);

  align-items: center;
  border-radius: var(--border-radius-radius-sm);
  background: var(--tooltip-color, var(--background-black, #141413));

  color: var(--tooltip-text-color, var(--text-white, #fff));
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22.4px;
`;

const Nubbin = styled.div`
  width: 0;
  height: 0;
`;

const nubbinStyles = stylesheet`
  .top, .top-start, .top-end {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid var(--tooltip-color, var(--background-black, #141413));
  }
  .left, .left-start, .left-end {
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 10px solid var(--tooltip-color, var(--background-black, #141413));  
  }
  .right, .right-start, .right-end {
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid var(--tooltip-color, var(--background-black, #141413));
  }
  .bottom, .bottom-start, .bottom-end {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid var(--tooltip-color, var(--background-black, #141413));
  }
`;

/**
 * A basic tooltip component with Academia styling. This component uses
 * {@link Popover} as its parent, adding some additional styling.
 *
 * Props:
 * - `placement`: Controls the placement of the tooltip relative to the activator.
 *                Choose between 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'bottom-start' | 'left-start' | 'right-start' | 'top-end' | 'bottom-end' | 'left-end' | 'right-end'
 * - `offset`: Controls the offset of the tooltip relative to the activator.
 * - `closeOnClick`: Controls whether the tooltip should close when the activator is clicked.
 * - `popoverRef`: A ref for the popover.
 * - `render`: A function that returns the tooltip content.
 * - `children`: The activator element.
 * - `mode`: Controls the Popover mode of the tooltip.
 *           Choose between 'click' | 'hover' | 'any'
 * - `tooltipColor`: Optional color for the tooltip and nubbin.
 * - `tooltipTextColor`: Optional color for the tooltip text.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Tooltip } from 'js/design_system/Tooltip';
 *
 * <Tooltip
 *   render={() => <p>I'm the tooltip content!</p>}
 * >
 *   <p>Click me to activate!</p>
 * </Tooltip>
 * ```
 */
export const Tooltip: React.FC<
  PopoverProps & { tooltipColor?: string; tooltipTextColor?: string }
> = ({
  mode = PopoverMode.Hover,
  placement = 'top',
  offset = 4,
  closeOnClick = false,
  popoverRef,
  render,
  children,
  tooltipColor,
  tooltipTextColor,
  ...props
}) => {
  const tooltipStyle = {} as React.CSSProperties & {
    '--tooltip-color'?: string;
    '--tooltip-text-color'?: string;
  };
  if (tooltipColor) {
    tooltipStyle['--tooltip-color'] = tooltipColor;
  }
  if (tooltipTextColor) {
    tooltipStyle['--tooltip-text-color'] = tooltipTextColor;
  }

  return (
    <Popover
      mode={mode}
      placement={placement}
      offset={offset}
      closeOnClick={closeOnClick}
      render={() => {
        return (
          <Container
            className={containerStyles[placement]}
            style={tooltipStyle}
          >
            <TooltipContainer ref={popoverRef}>{render()}</TooltipContainer>
            <Nubbin className={nubbinStyles[placement]} />
          </Container>
        );
      }}
      {...props}
    >
      {children}
    </Popover>
  );
};
